<template>
  <div class="package-panel">
    <slot name="summary-header" />

    <ul class="package-panel__products" :class="{ thin: thin }">
      <li
        class="package-panel__option"
        v-for="product in filteredSelectedProducts"
        :key="product.code"
        :class="{ disabled: isCellDisabled(product) }"
        data-testid="package-panel-option"
      >
        <div class="package-panel__option-image-wrapper">
          <img
            class="package-panel__option-image"
            v-if="productImage(product)"
            :src="productImage(product)"
            :alt="product.title"
          />
        </div>
        <h3 class="package-panel__option-name" :data-testid="product.type">
          {{ product.title }}
        </h3>
        <button
          class="btn inline-edit-btn"
          v-if="inlineEditingSupported(product)"
          @click="inlineEditAction(product)"
        >
          {{ inlineEditTitle(product) }}
        </button>
      </li>
    </ul>

    <slot name="summary-footer"></slot>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";

export default {
  name: "PackageSummary",
  props: {
    thin: {
      type: Boolean,
      required: false,
      default: false,
    },
    joiningTenant: {
      type: Boolean,
      required: false,
      default: false,
    },
    inlineEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideUnwantedProducts: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  computed: {
    ...mapState({
      products({ products }) {
        return products;
      },
      selectedProducts({ accountInformation }) {
        if (this.joiningTenant) {
          return accountInformation.products;
        } else {
          return this.$store.getters.selectedProducts;
        }
      },
    }),
    filteredSelectedProducts() {
      if (!this.hideUnwantedProducts) {
        return this.selectedProducts;
      }

      return this.selectedProducts.filter(
        (product) => !product.code.includes("_NONE"),
      );
    },
  },
  methods: {
    ...mapActions(["updatePreSignupLeadProductData"]),
    ...mapMutations({
      setProductOption: "SET_PRODUCT_OPTION",
      setPickedProductPrice: "SET_PICKED_PRODUCT_PRICE",
    }),
    productImage(productOption) {
      if (productOption.image !== "") {
        return productOption.image;
      }

      if (!this.products || this.products.length === 0) {
        return "";
      }

      const product = this.products.find(
        (product) => product.type === productOption.type,
      );
      const option = product.options.find(
        (op) => op.image && op.image.length > 0,
      );
      return option === undefined ? "" : option.image;
    },
    inlineEditingSupported(product) {
      return (
        this.inlineEditing &&
        (product.type === "Broadband" || product.type === "TV Licence")
      );
    },
    inlineEditTitle(product) {
      const isProductUnwanted = product.code.includes("_NONE");

      switch (product.type) {
        case "TV Licence":
          return isProductUnwanted ? "Add" : "Remove";
        default:
          return isProductUnwanted ? "Add" : "Change";
      }
    },
    inlineEditAction(product) {
      switch (product.type) {
        case "TV Licence": {
          const fullProductIndex = this.products.findIndex(
            (p) => p.type === product.type,
          );
          const fullProduct = this.products[fullProductIndex];
          let newProductOption;

          if (product.code.includes("_NONE")) {
            newProductOption = fullProduct.options.find(
              (op) => !op.code.includes("_NONE"),
            );
          } else {
            newProductOption = fullProduct.options.find((op) =>
              op.code.includes("_NONE"),
            );
          }

          this.setProductOption({
            type: product.type,
            code: newProductOption.code,
          });
          this.setPickedProductPrice({
            index: fullProductIndex,
            price: newProductOption.price.current,
          });

          this.updatePreSignupLeadProductData(newProductOption.priceId);
          break;
        }
        case "Broadband":
          this.$router.push("/products/Broadband");
          break;
        default:
          console.warn("Unhandled inline edit"); // eslint-disable-line
          break;
      }
    },
    isCellDisabled(product) {
      if (!this.inlineEditing) {
        return false;
      }

      return product.code.includes("_NONE");
    },
  },
};
</script>

<style lang="scss" scoped>
.package-panel__products {
  list-style-type: none;
  padding: 0;
  margin-bottom: 0;

  &.thin {
    margin: 0 auto;

    @include media(">=medium") {
      max-width: 60%;
    }
  }
}

.package-panel__option {
  display: flex;
  align-items: center;
  background: $brand-gradient;
  border-radius: $border-radius-small;
  color: white;

  margin-bottom: $spacing-tiny;

  &:last-child {
    margin-bottom: 0;
  }

  &.disabled {
    border: 2px solid $grey-dark;
    background: rgba($grey-dark, 0.25);
  }
}

.package-panel__option-name {
  font-size: 18px;
  margin: 0;
  flex-grow: 2;
  text-align: left;

  &:last-child {
    margin-bottom: 0;
  }
}

.package-panel__option-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;

  margin: 4px $spacing-small 4px 4px;

  background: white;
  min-width: 55px;
  min-height: 55px;
  width: 55px;
  height: 55px;
  border-radius: 8px;
}

.package-panel__option-image {
  max-width: 40px;
  max-height: 40px;
  margin: 0 auto;
}

.inline-edit-btn {
  align-self: normal;
  margin: 3px;
  min-width: 25%;
  padding: 0;
  font-size: $form-font-size;
  border-radius: $border-radius-small;
}
</style>
